/**
 * User role utility functions and constants
 * Runtime code extracted from types/user-roles.ts
 */
import { OrgRole, UserRole } from "@/types/user-roles";

// ============================================================================
// LABELS AND DESCRIPTIONS
// ============================================================================

/**
 * User role display names for UI
 */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.LYFE_ADMIN]: "Lyfe Admin",
  [UserRole.PROVIDER]: "Provider",
  [UserRole.ADMIN]: "Admin",
  [UserRole.DD_ANALYST]: "DD Analyst",
  [UserRole.NURSE]: "Nurse",
  [UserRole.STAFF]: "Staff",
  [UserRole.PATIENT]: "Patient",
  [UserRole.FAMILY]: "Family",
};

/**
 * Organization role display names for UI
 */
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  [OrgRole.CLINIC_ADMIN]: "Clinic Admin",
  [OrgRole.CLINIC_PROVIDER]: "Provider",
  [OrgRole.CLINIC_STAFF]: "Staff",
};

/**
 * User role descriptions
 */
export const USER_ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.LYFE_ADMIN]: "Platform administrator with cross-organization access",
  [UserRole.PROVIDER]: "Healthcare provider with access to patient care",
  [UserRole.ADMIN]: "System administrator with full access",
  [UserRole.DD_ANALYST]: "Due diligence analyst",
  [UserRole.NURSE]: "Nursing staff",
  [UserRole.STAFF]: "General staff member",
  [UserRole.PATIENT]: "Patient with access to their own portal",
  [UserRole.FAMILY]: "Family member with access to patient portals",
};

/**
 * Organization role descriptions
 */
export const ORG_ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  [OrgRole.CLINIC_ADMIN]: "Full control over clinic data, staff, and settings",
  [OrgRole.CLINIC_PROVIDER]: "Access to patients, encounters, and orders",
  [OrgRole.CLINIC_STAFF]: "Limited access to scheduling and basic patient info",
};

// ============================================================================
// LABEL GETTERS
// ============================================================================

/**
 * Get user role display name
 */
export function getUserRoleLabel(role: UserRole): string {
  return USER_ROLE_LABELS[role] || role;
}

/**
 * Get organization role display name
 */
export function getOrgRoleLabel(role: OrgRole | string): string {
  return ORG_ROLE_LABELS[role as OrgRole] || role;
}

/**
 * Get user role description
 */
export function getUserRoleDescription(role: UserRole): string {
  return USER_ROLE_DESCRIPTIONS[role] || "";
}

/**
 * Get user role initials
 */
export function getUserRoleInitials(role: UserRole): string {
  return role?.charAt(0)?.toUpperCase() + role?.slice(1);
}

// ============================================================================
// ROLE TYPE CHECKS
// ============================================================================

/**
 * Check if a role is a patient role
 */
export function isPatientRole(role: UserRole): boolean {
  return role === UserRole.PATIENT;
}

/**
 * Check if a role is a family role
 */
export function isFamilyRole(role: UserRole): boolean {
  return role === UserRole.FAMILY;
}

/**
 * Check if a role is a provider role (legacy check)
 */
export function isProviderRole(role: UserRole): boolean {
  return role === UserRole.PROVIDER || role === UserRole.NURSE || role === UserRole.DD_ANALYST;
}

/**
 * Check if a role is an admin role (platform-level admin)
 */
export function isAdminRole(role: UserRole): boolean {
  return role === UserRole.ADMIN || role === UserRole.LYFE_ADMIN;
}

/**
 * Check if user is a Lyfe Admin (cross-org access)
 */
export function isLyfeAdmin(role: UserRole): boolean {
  return role === UserRole.LYFE_ADMIN;
}

/**
 * Check if org role is clinic admin
 */
export function isClinicAdmin(orgRole: OrgRole | string | null | undefined): boolean {
  return orgRole === OrgRole.CLINIC_ADMIN;
}

/**
 * Check if org role is clinic provider
 */
export function isClinicProvider(orgRole: OrgRole | string | null | undefined): boolean {
  return orgRole === OrgRole.CLINIC_PROVIDER;
}

/**
 * Check if org role is clinic staff
 */
export function isClinicStaff(orgRole: OrgRole | string | null | undefined): boolean {
  return orgRole === OrgRole.CLINIC_STAFF;
}

/**
 * Check if org role has provider-level access (admin or provider)
 */
export function hasProviderAccess(orgRole: OrgRole | string | null | undefined): boolean {
  return orgRole === OrgRole.CLINIC_ADMIN || orgRole === OrgRole.CLINIC_PROVIDER;
}

// ============================================================================
// ROLE LISTS AND FEATURES
// ============================================================================

/**
 * Get all user roles
 */
export const USER_ROLES = Object.values(UserRole);

/**
 * Get all organization roles
 */
export const ORG_ROLES = Object.values(OrgRole);

/**
 * Default features for new organizations
 */
export const DEFAULT_ORG_FEATURES = ["ai_insights", "patient_timeline", "documents", "scheduling"];

/**
 * All available organization features
 */
export const ALL_ORG_FEATURES = [
  "ai_insights",
  "patient_timeline",
  "documents",
  "scheduling",
  "ehr_sync",
  "zus_integration",
  "drchrono_integration",
  "analytics",
  "telehealth",
  "care_gaps",
  "risk_assessment",
];
